import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeft, Eye, EyeOff, GripVertical, Plus } from "lucide-react";
import {
  CMS_BLOCK_KINDS,
  CMS_BLOCK_LABELS,
  createCmsBlock,
  type CmsBlock,
  type CmsBlockKind,
} from "@gaole/shared";
import { api } from "../../lib/api";
import { toast } from "../../stores/toast";
import { useAuth } from "../../stores/auth";
import { CmsBlockRenderer } from "../../components/cms/CmsBlockRenderer";
import { ImageUploadField } from "../../components/ImageUploadField";
import {
  Button,
  Input,
  PageLoader,
  SearchSelect,
  SettingsCard,
  Textarea,
  Toggle,
} from "../../components/ui";

interface CmsPageDetail {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  blocks: CmsBlock[];
  status: "DRAFT" | "PUBLISHED";
  showInFooter: boolean;
}

function SortableBlockRow({
  block,
  onToggle,
  onUpdate,
}: {
  block: CmsBlock;
  onToggle: () => void;
  onUpdate: (next: CmsBlock) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="rounded-xl border border-line bg-white/[0.02] p-3">
      <div className="flex items-center gap-2">
        <button type="button" className="cursor-grab text-white/40 hover:text-white/70" {...attributes} {...listeners}>
          <GripVertical size={16} />
        </button>
        <span className="flex-1 text-sm font-medium">{CMS_BLOCK_LABELS[block.kind]}</span>
        <button type="button" className="text-white/50 hover:text-white" onClick={onToggle}>
          {block.enabled === false ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>

      {block.enabled !== false && block.kind === "heading" && (
        <div className="mt-2 space-y-2">
          <SearchSelect
            label="Level"
            options={[
              { value: "1", label: "Heading 1" },
              { value: "2", label: "Heading 2" },
            ]}
            value={String(block.level)}
            onChange={(v) => onUpdate({ ...block, level: Number(v) === 1 ? 1 : 2 })}
          />
          <Input label="Text" value={block.text} onChange={(e) => onUpdate({ ...block, text: e.target.value })} />
        </div>
      )}
      {block.enabled !== false && block.kind === "paragraph" && (
        <Textarea
          className="mt-2"
          label="Text"
          rows={4}
          value={block.text}
          onChange={(e) => onUpdate({ ...block, text: e.target.value })}
        />
      )}
      {block.enabled !== false && block.kind === "image" && (
        <div className="mt-2 space-y-2">
          <ImageUploadField
            label="Image"
            max={1}
            value={block.url ? [block.url] : []}
            onChange={(urls) => onUpdate({ ...block, url: urls[0] ?? "" })}
          />
          <Input label="Alt text" value={block.alt ?? ""} onChange={(e) => onUpdate({ ...block, alt: e.target.value })} />
          <Input
            label="Caption"
            value={block.caption ?? ""}
            onChange={(e) => onUpdate({ ...block, caption: e.target.value })}
          />
        </div>
      )}
      {block.enabled !== false && block.kind === "quote" && (
        <div className="mt-2 space-y-2">
          <Textarea label="Quote" rows={3} value={block.text} onChange={(e) => onUpdate({ ...block, text: e.target.value })} />
          <Input
            label="Attribution"
            value={block.attribution ?? ""}
            onChange={(e) => onUpdate({ ...block, attribution: e.target.value })}
          />
        </div>
      )}
      {block.enabled !== false && block.kind === "cta" && (
        <div className="mt-2 space-y-2">
          <Input label="Label" value={block.label} onChange={(e) => onUpdate({ ...block, label: e.target.value })} />
          <Input label="Link" value={block.href} onChange={(e) => onUpdate({ ...block, href: e.target.value })} />
        </div>
      )}
    </div>
  );
}

export function CmsPageEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canWrite = can("cms.write");

  const detailQuery = useQuery({
    queryKey: ["cms-page", id],
    queryFn: () => api<{ page: CmsPageDetail }>(`/cms/pages/${id}`).then((r) => r.page),
    enabled: !!id,
  });

  const [blocks, setBlocks] = useState<CmsBlock[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (detailQuery.data) {
      setBlocks(detailQuery.data.blocks);
      setDirty(false);
    }
  }, [detailQuery.data]);

  const saveMut = useMutation({
    mutationFn: () => api(`/cms/pages/${id}`, { method: "PUT", body: { blocks } }),
    onSuccess: () => {
      toast("success", "Saved");
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["cms-page", id] });
      void queryClient.invalidateQueries({ queryKey: ["cms-pages"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const publishMut = useMutation({
    mutationFn: () => api(`/cms/pages/${id}/publish`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Published");
      void queryClient.invalidateQueries({ queryKey: ["cms-page", id] });
      void queryClient.invalidateQueries({ queryKey: ["cms-pages"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks((items) => {
      const oldIndex = items.findIndex((b) => b.id === active.id);
      const newIndex = items.findIndex((b) => b.id === over.id);
      setDirty(true);
      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  if (detailQuery.isLoading || !detailQuery.data) return <PageLoader />;
  const page = detailQuery.data;

  function updateBlock(blockId: string, next: CmsBlock) {
    setBlocks((items) => items.map((b) => (b.id === blockId ? next : b)));
    setDirty(true);
  }

  function toggleBlock(blockId: string) {
    setBlocks((items) =>
      items.map((b) => (b.id === blockId ? { ...b, enabled: b.enabled === false ? true : false } : b)),
    );
    setDirty(true);
  }

  function addBlock(kind: CmsBlockKind) {
    setBlocks((items) => [...items, createCmsBlock(kind)]);
    setDirty(true);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/content/pages"
          className="inline-flex items-center gap-1 text-xs font-semibold text-white/60 hover:text-white"
        >
          <ArrowLeft size={14} /> Pages
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg font-bold">{page.title}</p>
          <p className="text-[11px] text-white/45">/pages/{page.slug} · {page.status}</p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <SettingsCard title="Blocks" description="Drag to reorder. Toggle visibility per block.">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {blocks.map((block) => (
                  <SortableBlockRow
                    key={block.id}
                    block={block}
                    onToggle={() => toggleBlock(block.id)}
                    onUpdate={(next) => updateBlock(block.id, next)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {canWrite && (
            <div className="mt-4 flex flex-wrap gap-2">
              {CMS_BLOCK_KINDS.map((kind) => (
                <Button key={kind} size="sm" variant="outline" onClick={() => addBlock(kind)}>
                  <Plus size={14} className="mr-1" /> {CMS_BLOCK_LABELS[kind]}
                </Button>
              ))}
            </div>
          )}
        </SettingsCard>

        <SettingsCard title="Preview" description="How this page will look on the storefront.">
          <div className="rounded-2xl border border-line bg-brand-dark/40 p-4">
            <CmsBlockRenderer blocks={blocks} />
          </div>
        </SettingsCard>
      </div>

      {canWrite && (
        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 rounded-xl border border-line bg-surface/95 p-3 backdrop-blur">
          <Button size="sm" variant="outline" disabled={!dirty} onClick={() => setBlocks(page.blocks)}>
            Cancel
          </Button>
          <Button size="sm" disabled={saveMut.isPending || !dirty} onClick={() => saveMut.mutate()}>
            Save
          </Button>
          {page.status === "DRAFT" && (
            <Button size="sm" disabled={publishMut.isPending} onClick={() => publishMut.mutate()}>
              Publish
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
